import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import type { ReactNode } from "react";
import {
  ContractDateReminderType,
  DefaultNotificationSettingsDto,
  FrameworkValueReminderDto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useSub } from "../../../common/subscription/useSub";
import { BidDeliveryDeadlineSetting } from "./BidDeliveryDeadlineSetting";
import { BidDeliverySettingForm } from "./BidDeliverySettingForm";
import { ContractDateReminderSetting } from "./ContractDateReminderSetting";
import { ContractNotificationSettingForm } from "./ContractDateReminderSettingForm";
import { ContractTaskDueDateReminderSetting } from "./ContractTaskDueDateReminderSetting";
import { ContractTaskNotificationSettingForm } from "./ContractTaskNotificationSettingForm";
import { FrameworkValueReminderSetting } from "./FrameworkValueReminderSetting";
import { FrameworkValueReminderSettingForm } from "./FrameworkValueReminderSettingForm";
import { RecurringContractReminderSetting } from "./RecurringContractReminderSetting";
import { RecurringContractReminderSettingForm } from "./RecurringContractReminderSettingForm";
import { SupplierNotificationReceiverForm } from "./SupplierNotificationReceiverForm";
import { hasReceivers, SupplierNotificationReceiverSetting } from "./SupplierNotificationReceiverSetting";

export const initSettings = (): DefaultNotificationSettingsDto => ({
  contractNotificationSettings: {
    startDateNotifications: [],
    endDateNotifications: [],
    renewalDateNotifications: [],
    warrantyExpirationNotifications: [],
    terminationDateNotifications: [],
    noticePeriodNotifications: [],
    optionExecutionDeadlineNotifications: [],
    optionStartDateNotifications: [],
    noEndDateNotifications: [],
  },
  contractTaskNotificationSettings: {
    dueDateNotifications: [],
  },
  sourcingEventNotificationSettings: {
    bidDeliveryDeadlineNotifications: [],
  },
  frameworkAgreementNotificationSettings: {
    valueNotifications: [],
  },
});

export const frameworkValueReminders = (settings?: DefaultNotificationSettingsDto | null): FrameworkValueReminderDto[] =>
  settings?.frameworkAgreementNotificationSettings?.valueNotifications ?? [];

const AddSetting = ({ isEmpty, children }: { isEmpty: boolean; children: ReactNode }) => {
  const { canUpdateOrganization } = useSub();
  if (canUpdateOrganization) return <>{children}</>;
  return isEmpty ? (
    <Text fontSize="sm" color="smMuted">
      {t("No notifications set")}
    </Text>
  ) : null;
};

export const NotificationSettings = () => {
  const authState = useLoggedInWithOrgContextState();
  const sub = useSub();

  return (
    <Accordion allowMultiple>
      {sub.hasContractAccess && (
      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box as="span" flex="1" textAlign="left" fontWeight="semibold" fontSize={"lg"}>
              {t("Notifications for contracts")}
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb="4">
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Start date")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.startDateNotifications.map(
              (setting, i) => (
                <ContractDateReminderSetting
                  key={i}
                  setting={setting}
                  type={ContractDateReminderType.StartDateReminder}
                />
              )
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.startDateNotifications?.length}>
              <ContractNotificationSettingForm type={ContractDateReminderType.StartDateReminder} />
            </AddSetting>
          </Flex>
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("End date")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.endDateNotifications.map(
              (setting, i) => (
                <ContractDateReminderSetting
                  key={i}
                  setting={setting}
                  type={ContractDateReminderType.EndDateReminder}
                />
              )
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.endDateNotifications?.length}>
              <ContractNotificationSettingForm type={ContractDateReminderType.EndDateReminder} />
            </AddSetting>
          </Flex>
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Warranty expiration date")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.warrantyExpirationNotifications.map(
              (setting, i) => (
                <ContractDateReminderSetting
                  key={i}
                  setting={setting}
                  type={ContractDateReminderType.WarrantyExpirationDateReminder}
                />
              )
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.warrantyExpirationNotifications?.length}>
              <ContractNotificationSettingForm type={ContractDateReminderType.WarrantyExpirationDateReminder} />
            </AddSetting>
          </Flex>
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Renewal date")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.renewalDateNotifications.map(
              (setting, i) => (
                <ContractDateReminderSetting
                  key={i}
                  setting={setting}
                  type={ContractDateReminderType.RenewalDateReminder}
                />
              )
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.renewalDateNotifications?.length}>
              <ContractNotificationSettingForm type={ContractDateReminderType.RenewalDateReminder} />
            </AddSetting>
          </Flex>
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Termination date")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.terminationDateNotifications.map(
              (setting, i) => (
                <ContractDateReminderSetting
                  key={i}
                  setting={setting}
                  type={ContractDateReminderType.TerminationDateReminder}
                />
              )
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.terminationDateNotifications?.length}>
              <ContractNotificationSettingForm type={ContractDateReminderType.TerminationDateReminder} />
            </AddSetting>
          </Flex>
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Notice period start date")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.noticePeriodNotifications.map(
              (setting, i) => (
                <ContractDateReminderSetting
                  key={i}
                  setting={setting}
                  type={ContractDateReminderType.NoticePeriodReminder}
                />
              )
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.noticePeriodNotifications?.length}>
              <ContractNotificationSettingForm type={ContractDateReminderType.NoticePeriodReminder} />
            </AddSetting>
          </Flex>
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Option execution deadline")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.optionExecutionDeadlineNotifications.map(
              (setting, i) => (
                <ContractDateReminderSetting
                  key={i}
                  setting={setting}
                  type={ContractDateReminderType.OptionExecutionDeadlineReminder}
                />
              )
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.optionExecutionDeadlineNotifications?.length}>
              <ContractNotificationSettingForm type={ContractDateReminderType.OptionExecutionDeadlineReminder} />
            </AddSetting>
          </Flex>
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Option start date")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.optionStartDateNotifications.map(
              (setting, i) => (
                <ContractDateReminderSetting
                  key={i}
                  setting={setting}
                  type={ContractDateReminderType.OptionStartDateReminder}
                />
              )
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.optionStartDateNotifications?.length}>
              <ContractNotificationSettingForm type={ContractDateReminderType.OptionStartDateReminder} />
            </AddSetting>
          </Flex>
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Contracts with no end date")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.noEndDateNotifications.map(
              (setting, i) => <RecurringContractReminderSetting key={i} setting={setting} />
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractNotificationSettings.noEndDateNotifications?.length}>
              <RecurringContractReminderSettingForm />
            </AddSetting>
          </Flex>
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Framework agreement's ceiling value")}
            </Text>
            {frameworkValueReminders(authState.selectedOrg.defaultNotificationSettings).map((setting, i) => (
              <FrameworkValueReminderSetting key={i} setting={setting} />
            ))}
            <AddSetting isEmpty={frameworkValueReminders(authState.selectedOrg.defaultNotificationSettings).length === 0}>
              <FrameworkValueReminderSettingForm />
            </AddSetting>
          </Flex>
        </AccordionPanel>
      </AccordionItem>
      )}
      {sub.hasContractAccess && (
      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box as="span" flex="1" textAlign="left" fontWeight="semibold" fontSize={"lg"}>
              {t("Notifications for contract tasks")}
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb="4">
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Due date")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.contractTaskNotificationSettings.dueDateNotifications.map(
              (setting, i) => <ContractTaskDueDateReminderSetting key={i} setting={setting} />
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.contractTaskNotificationSettings.dueDateNotifications?.length}>
              <ContractTaskNotificationSettingForm />
            </AddSetting>
          </Flex>
        </AccordionPanel>
      </AccordionItem>
      )}
      {sub.hasBuyerAccess && (
      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box as="span" flex="1" textAlign="left" fontWeight="semibold" fontSize={"lg"}>
              {t("Notifications for sourcing events")}
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb="4">
          <Flex flexDirection={"column"} pb="4">
            <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
              {t("Bid delivery deadline")}
            </Text>
            {authState.selectedOrg.defaultNotificationSettings?.sourcingEventNotificationSettings.bidDeliveryDeadlineNotifications.map(
              (setting, i) => <BidDeliveryDeadlineSetting key={i} setting={setting} />
            )}
            <AddSetting isEmpty={!authState.selectedOrg.defaultNotificationSettings?.sourcingEventNotificationSettings.bidDeliveryDeadlineNotifications?.length}>
              <BidDeliverySettingForm />
            </AddSetting>
          </Flex>
        </AccordionPanel>
      </AccordionItem>
      )}
      {!sub.hasBuyerAccess && (
        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold" fontSize={"lg"}>
                {t("Supplier notifications")}
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb="4">
            <Flex flexDirection={"column"} pb="4">
              <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
                {t("Sourcing event invitations")}
              </Text>
              {authState.selectedOrg.defaultNotificationSettings?.supplierNotificationSettings?.inviteReceivers && (
                <SupplierNotificationReceiverSetting
                  receivers={authState.selectedOrg.defaultNotificationSettings.supplierNotificationSettings.inviteReceivers}
                  receiverType="inviteReceivers"
                />
              )}
              <AddSetting isEmpty={!hasReceivers(authState.selectedOrg.defaultNotificationSettings?.supplierNotificationSettings?.inviteReceivers)}>
                <SupplierNotificationReceiverForm receiverType="inviteReceivers" />
              </AddSetting>
            </Flex>
            <Flex flexDirection={"column"} pb="4">
              <Text fontWeight={"bold"} fontSize={"sm"} pb="1">
                {t("Messages from buyer")}
              </Text>
              {authState.selectedOrg.defaultNotificationSettings?.supplierNotificationSettings?.messageReceivers && (
                <SupplierNotificationReceiverSetting
                  receivers={authState.selectedOrg.defaultNotificationSettings.supplierNotificationSettings.messageReceivers}
                  receiverType="messageReceivers"
                />
              )}
              <AddSetting isEmpty={!hasReceivers(authState.selectedOrg.defaultNotificationSettings?.supplierNotificationSettings?.messageReceivers)}>
                <SupplierNotificationReceiverForm receiverType="messageReceivers" />
              </AddSetting>
            </Flex>
          </AccordionPanel>
        </AccordionItem>
      )}
    </Accordion>
  );
};
